import { createAsyncThunk } from "@reduxjs/toolkit"
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../http"
import { AppId, AppThunkAPIType } from "../../../app/appTypes"
import { ValidatedObject } from "../validationProcess/validatedObjectApi"
import { loadValidationProcessStageOverview } from "../validationProcess/validationProcessApi"
import { DocumentProcess } from "../../documents/documentSlice"

export type ProjectProcessStatus = 'D' | 'I' | 'H' | 'F' | 'C'
export type ProjectProcessType = 'VALIDATION' | 'SUPPLIER_DOCUMENTATION'
export type ProcessStageStatus = 'NOTSTARTED' | 'IN_PROGRESS' | 'ONHOLD' | 'FINISHED' | 'CANCELLED' | 'SKIPPED'
export type ProcessTeamMemberRole = 'M' | 'C' | 'O'

export interface ProjectProcessHeader {
    id: AppId,
    no: string,
    name: string,
    description: string,
    status: string,
    deviceId: string,
    computerizedSystemId: string,
    ursOfferId: string,
    createdBy: string,
    createdByFullName: string,
    startDate: Date,
    finishDate: Date,
    createDate: Date,
    modifiedBy: string,
    modifiedByFullName: string,
    modifyDate: Date,
    projectId: number,
    projectName: string,
    projectNo: string,
    guid: string,
    structureId: number;
    scenario: string;
    processType: ProjectProcessType
    validatedObject?: ValidatedObject
    lastActionComment: string
}

export interface ProjectProcessTeamMember {
    id: AppId,
    user: string,
    firstName: string,
    surname: string,
    isExternal?: boolean,
    position: string,
    companyName: string,
    role: ProcessTeamMemberRole,
    processId: number
}

export interface ProjectProcessStage {
    id: AppId,
    name: string,
    no: string,
    responsibleFullName: string,
    createDate: Date,
    processId: number,
    status: string,
    type: string
}

export type ProjectProcess = ProjectProcessHeader & {
    processTeamMembers: ProjectProcessTeamMember[]
    projectProcessStages: ProjectProcessStage[]
}

export interface CreateProcessTeamMemberPayload {
    user: string,
    processId: string,
    role: string
}

export interface InviteProcessTeamMemberPayload {
    userEmail: string,
    processId: string,
    role: string
}

export interface DescriptionFieldPayload {
    fieldName: string
    text: string
}

export interface StatusResponse {
    status: string
}

export interface ProjectActionArgs {
    id: AppId,
    args: { password: string }
}

export interface ProjectStatusActionArgs {
    id: AppId,
    args: { password: string } & StatusResponse
}


export const loadProjectProcesses = createAsyncThunk<ProjectProcessHeader[], void, AppThunkAPIType>(
    "projectProcesses/loadAll", async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: "api/project-process" }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcessHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadDocumentationProcesses = createAsyncThunk<ProjectProcessHeader[], void, AppThunkAPIType>(
    "projectProcesses/load-documentation", async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: "api/documentation-processes" }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcessHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })



export const loadProjectProcess = createAsyncThunk<ProjectProcess, string, AppThunkAPIType>("projectProcesses/loadProcess", async (id, { dispatch, rejectWithValue }) => {
    const result = await dispatch(getWithAuth({
        url: `api/project-process/${id}`,
    }))
    const { payload } = result
    if (getWithAuth.fulfilled.match(result)) {
        return payload as ProjectProcess
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const loadDocumentProcess = createAsyncThunk<DocumentProcess, string, AppThunkAPIType>("projectProcesses/loadDocumentsProcesses", async (docId, { dispatch, rejectWithValue }) => {
    const result = await dispatch(getWithAuth({
        url: `api/project-process/active-for-document/${docId}`,
    }))
    const { payload } = result
    if (getWithAuth.fulfilled.match(result)) {
        const procs = payload as ProjectProcess
        // console.log(`Active processes for document ${docId}:`, procs)
        const stages = await dispatch(loadValidationProcessStageOverview(procs.id)).unwrap()
        // console.log('Stages:', stages)
        return {
            processId: procs.id,
            stages,
        } as DocumentProcess
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const loadForProject = createAsyncThunk<ProjectProcessHeader[], string, AppThunkAPIType>("projectProcesses/loadForProject", async (id, { dispatch, rejectWithValue }) => {
    const result = await dispatch(getWithAuth({
        url: `api/project-process/load-for-project/${id}`,
    }))
    const { payload } = result
    if (getWithAuth.fulfilled.match(result)) {
        return payload as ProjectProcessHeader[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const loadProcessTeamMembers = createAsyncThunk<ProjectProcessTeamMember[], string, AppThunkAPIType>(
    "projectProcessesTM/load", async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/process-team-member/${id}`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcessTeamMember[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const createProcessTeamMember = createAsyncThunk<ProjectProcessTeamMember, CreateProcessTeamMemberPayload, AppThunkAPIType>(
    "projectProcessesTM/create",
    async (createPayload: CreateProcessTeamMemberPayload, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: "api/process-team-member",
            payload: createPayload,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcessTeamMember
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const inviteProcessTeamMember = createAsyncThunk<ProjectProcessTeamMember, InviteProcessTeamMemberPayload, AppThunkAPIType>(
    "projectProcessesTM/invite",
    async (invitePayload: InviteProcessTeamMemberPayload, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: "api/process-team-member/invite",
            payload: invitePayload,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcessTeamMember
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const deleteProcessTeamMember = createAsyncThunk<AppId, AppId, AppThunkAPIType>(
    "process-team-member/delete",
    async (id: AppId, { rejectWithValue, dispatch }) => {
        const result = await dispatch(deleteWithAuth({ url: `api/process-team-member/${id}` }))
        const { payload } = result
        if (deleteWithAuth.fulfilled.match(result)) {
            return id
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)


export type ProcessActionType =
    | "startProcess"
    | "pauseProcess"
    | "cancelProcess"
    | "finishProcess";

export interface PerformProjectAction {
    processId: AppId,
    action: ProcessActionType,
    actionComment: string,
    password: string
}

export const performProcessAction = createAsyncThunk<ProjectProcess, PerformProjectAction, AppThunkAPIType>(
    "process/perform-action",
    async ({ processId, password, action, actionComment }, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: `api/project-process/${processId}/action`,
            payload: { processId, password, action, actionComment }
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcess
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)


export const deletepProcess = createAsyncThunk<AppId, ProjectActionArgs, AppThunkAPIType>(
    "process/delete",
    async ({ id, args }, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: `api/project-process/${id}`,
            payload: args
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return id
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateProjectProcessName = createAsyncThunk<ProjectProcess, { id: AppId, data: DescriptionFieldPayload }, AppThunkAPIType>(
    "process/update",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/project-process/${id}`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcess
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const linkDocumentToProcess = createAsyncThunk<ProjectProcess, { documentId: AppId, processId: AppId }, AppThunkAPIType>(
    "process/updateDocumentProcess",
    async ({ processId, documentId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/project-process/${processId}/link`,
            payload: {
                documentId: documentId,
                processId: processId
            },
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcess
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateProjectProcessStatus = createAsyncThunk<StatusResponse, ProjectStatusActionArgs, AppThunkAPIType>(
    "projects/update-status",
    async ({ id, args }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/project-process/${id}/status`,
            payload: args ?? {},
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as StatusResponse
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)


export interface ProcessExistsResponse {
    exists: boolean
}

export const processExists = createAsyncThunk<ProcessExistsResponse, string, AppThunkAPIType>(
    "project-process/exists", async (processNo, { dispatch, rejectWithValue }) => {
        const rawUrl = `api/project-process/exists?processNo=${processNo}`
        const result = await dispatch(getWithAuth({
            url: encodeURI(rawUrl),
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as ProcessExistsResponse
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

